/**
 * "Your plan summary" — the aged care module's right pane, as ONE panel.
 *
 * WHY THIS EXISTS. This is the second major step away from the dismissible Insight Panel. The first
 * moved the walk-in position inline (WalkInPositionNote); this one gathers what a client actually
 * has to read together into a single surface that never moves:
 *
 *     what care costs  →  what it does to the position  →  why  →  the projections
 *
 * The rail used to hold two separate cards — Cost Overview, then Financial Impact — with the
 * plain-words narrative somewhere further down the page entirely. Owner, 2026-08-26, working at
 * 100% zoom: "this is really hard to navigate, im having to scroll both up and down the screen to
 * see what the hell is going on - its terrible." Cause on the left, effect on the right, and the
 * explanation somewhere else again, so every input change meant hunting for what moved.
 *
 * The owner's own design, and it is better than the alternative that was proposed (a sticky strip of
 * three figures across the top): a strip steals vertical height — the scarcest thing at 100% — and
 * still leaves the narrative far from the numbers it explains.
 *
 * WHAT THIS COMPONENT IS NOT. It computes nothing. Every figure is passed in, already derived by the
 * callers that owned them before — agedCareBaseline.ts for the benchmark, agedCareCostsEngine for
 * the fees, FinancialImpactPanel for the four tiles, StrategyPlanRecap for the narrative. That is
 * deliberate and it is the safety property of this change: the two-baselines class of bug (a panel
 * and a summary each deriving the same number their own way, disagreeing by $97,649, with neither
 * half's unit test able to see it) is designed out by there being one panel, not by being tested for.
 *
 * LAYOUT. This pane is ~391px at the owner's 100% zoom (1330px viewport) and ~595px at 1920. Nothing
 * in here may subdivide on a `md:`/`lg:` prefix: those measure the VIEWPORT, not this pane. See
 * agedCareResponsiveLayout.test.ts.
 */
import React from 'react';
import { Eye } from 'lucide-react';
import { GradientText } from '../../components/ui/index.tsx';

export interface PlanSummaryPanelProps {
    /**
     * The strategy being summarised, in the client's words — or null before one is built, when this
     * panel shows the walk-in position instead. Never a placeholder: a panel that cannot say what it
     * is describing is worse than one that says it is describing nothing yet.
     */
    strategyName?: string | null;
    /** What the "before" column IS ("no plan"), named so a difference can be identified. */
    baselineLabel?: string;
    /** The dark Cost Overview block, unchanged — passed in rather than rebuilt. */
    costOverview: React.ReactNode;
    /**
     * What the cost block IS, in this module's words.
     *
     * Defaults to Aged Care's "What care costs". A village entry contribution, a downsizing sale
     * and a granny flat contribution are not care costs, and labelling them so told the reader the
     * page was describing something it was not.
     */
    costOverviewLabel?: string;
    /** The four before/after tiles: FinancialImpactPanel, rendered by the caller that owns its data. */
    financialImpact: React.ReactNode;
    /** One or two sentences. Anything longer belongs behind `onReadFullExplanation`. */
    inBrief?: React.ReactNode;
    /** Opens the full plain-words explanation. Omitted when there is no strategy to explain. */
    onReadFullExplanation?: (() => void) | null;
    /** Opens the existing shared ProjectionsModal — the same one the View Projections link opens. */
    onViewProjections: () => void;
    /** How long the money lasts. Added in the next step; absent until then. */
    liquidity?: React.ReactNode;
    /**
     * What to call this panel when there is no strategy yet, and what to say about it.
     *
     * Defaults describe Aged Care, where the care is happening either way, so the no-strategy state
     * genuinely IS the benchmark every strategy is measured against.
     *
     * Support at Home has a state Aged Care does not: no SERVICE PLAN yet. There the figures are not
     * a baseline at all — they are the client's position today, with an illustrative package
     * standing in for services nobody has chosen. Calling that "the baseline" told the reader a
     * placeholder was the thing to beat (owner-caught, 2026-08-27). A panel must say what it is
     * showing, and the two states are not the same thing.
     */
    noStrategyHeading?: string;
    noStrategyNote?: React.ReactNode;
}

const PlanSummaryPanel = ({
    strategyName,
    baselineLabel,
    costOverview,
    costOverviewLabel = 'What care costs',
    financialImpact,
    inBrief,
    onReadFullExplanation,
    onViewProjections,
    liquidity,
    noStrategyHeading = 'No plan yet — the walk-in position',
    noStrategyNote,
}: PlanSummaryPanelProps) => (
    /*
     * THE PANEL MUST FIT THE SCREEN, or `sticky` cannot help it.
     *
     * A sticky element TALLER than the viewport does not pin its top — the browser scrolls it until
     * its bottom edge arrives, so working further down the page (the funding sliders, say) carried
     * the heading and "In brief" off the top and the reader had to scroll back up to see what their
     * own change had done (owner, 2026-08-27, at 80% zoom).
     *
     * So the panel is capped to the viewport and split: the heading and "In brief" — the answer —
     * never move, and the supporting detail scrolls inside. `100vh` is the right unit even though
     * the app scrolls an inner region, because the shell is h-screen and this pane is measured
     * against the same window. The 8rem covers the page header and the sticky offset above it.
     */
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-card overflow-hidden xl:flex xl:flex-col xl:max-h-[calc(100vh-8rem)]">
        <div className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-200/80 bg-gradient-to-br from-slate-50/90 via-white to-teal-50/40">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Your plan summary</p>
            <h3 className="text-lg font-bold tracking-tight text-slate-900 leading-snug">
                {strategyName ? <GradientText>{strategyName}</GradientText> : noStrategyHeading}
            </h3>
            <p className="mt-1 text-[13px] text-slate-500 leading-relaxed">
                {strategyName ? (
                    <>
                        Measured against <span className="font-semibold text-slate-700">{baselineLabel || 'no plan'}</span>.
                        The grey figure is that baseline; the bold figure is this plan.
                    </>
                ) : noStrategyNote ?? (
                    <>
                        This <span className="font-semibold text-slate-700">is</span> the baseline. Build a strategy on
                        the left and every figure here is restated against it.
                    </>
                )}
            </p>
        </div>

        {/*
          * ORDER: the answer, then the working.
          *
          * "In brief" and the way into the full explanation sit directly under the heading, above
          * the cost table and the runway. Owner, 2026-08-26, working with the allocators open:
          * "currently they need to scroll down the page to see this". The one sentence saying what
          * the plan does is the thing a client needs at a glance; the tables supporting it are what
          * they read second, and an adviser talking someone through it reads top to bottom anyway.
          */}
        {/* The answer, pinned. Everything below this scrolls. */}
        {inBrief && (
            <div className="shrink-0 px-5 pt-4 pb-1">
                <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400 mb-2">In brief</p>
                <div className="rounded-lg bg-slate-50 border border-slate-200/70 border-l-[3px] border-l-blue-500 px-3 py-2.5 text-[13px] leading-relaxed text-slate-700">
                    {inBrief}
                </div>
                {onReadFullExplanation && (
                    <button
                        type="button"
                        onClick={onReadFullExplanation}
                        className="mt-2 w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                        Read the full explanation
                        <span aria-hidden className="text-blue-400">→</span>
                    </button>
                )}
            </div>
        )}

        <div className="px-5 py-4 space-y-5 xl:min-h-0 xl:overflow-y-auto">
            <section>
                <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400 mb-2">{costOverviewLabel}</p>
                {costOverview}
            </section>

            {liquidity && (
                <section>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400 mb-2">
                        How long the money lasts
                    </p>
                    {liquidity}
                </section>
            )}

            <section>{financialImpact}</section>

            {/* The projections stay at the foot: they are where you go AFTER reading the panel,
                not a summary of it. A button rather than an expander — the modal is a full screen
                of charts and a year-by-year table, which cannot live inside a 391px pane. */}
            <button
                type="button"
                onClick={onViewProjections}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
                <span className="flex items-center gap-2"><Eye size={15} aria-hidden /> See the projections</span>
                <span aria-hidden className="text-blue-400">→</span>
            </button>
        </div>
    </div>
);

export default PlanSummaryPanel;
